import React from 'react'

import { DATE_TIME_FORMAT, formatDateTime } from '@app/common/helpers/DateTime/DateTime'
import { fileIcons } from '@app/common/icons/FileIcons'
import { logger } from '@app/common/logger/logger'
import { CustomerDocument, LawyerDocument } from '@app/graphql/types'
import { useIntl } from '@app/intl/Intl'
import { bemJoin } from '@app/lib/bem'
import { openDocuments } from '@app/modules/Auth/documentLink'
import { useMobile } from '@app/services/useMobile'

import { DocumentActions } from './components/DocumentActions'
import { useDocumentCardHandlers } from './hooks/useDocumentCardHandlers'
import { useDocumentMetadata } from './hooks/useDocumentMetadata'

import './DocumentCard.scss'

export type DocumentCardProps = {
  document: CustomerDocument | LawyerDocument
  isNewDocument?: boolean
  onCheckmarkClick?: (checked: boolean) => void
  onRenameClick?: () => void
  onRemove?: () => void
}

export const DocumentCard: React.FC<DocumentCardProps> = ({
  document,
  isNewDocument,
  onCheckmarkClick,
  onRenameClick,
  onRemove
}) => {
  const b = bemJoin('document-card')
  const { formatMessage } = useIntl()
  const { isMobile } = useMobile()

  const { isChecked, fileUrl, fileFormat, date } = useDocumentMetadata(document)
  const handlers = useDocumentCardHandlers(isChecked, onCheckmarkClick, onRenameClick, onRemove)

  const handlePreviewClick = (event: React.MouseEvent) => {
    event.preventDefault()

    if (fileUrl) {
      openDocuments([fileUrl]).catch((error) => {
        logger.error(`unable to openDocuments on DocumentCard with ${error.message}`)
      })
    }
  }

  return (
    <div className={b({ new: isNewDocument })}>
      <a
        href={''}
        onClick={handlePreviewClick}
        className={b('preview', { notClickable: !fileUrl })}
        target='_blank'
        rel='noreferrer'
      >
        <img src={fileIcons[fileFormat] || fileIcons.UNKNOWN} alt={fileFormat} />
      </a>

      <div className={b('content')}>
        {date && (
          <div className={b('date')}>{formatDateTime(date, DATE_TIME_FORMAT.DATE_TIME)}</div>
        )}

        <button
          onClick={isMobile ? () => {} : handlers.handleEditClick}
          className={b('name', { clickable: !isMobile && !!onRenameClick })}
        >
          {document.name}
        </button>

        {isChecked && (
          <div className={b('checked-text')}>{formatMessage({ id: 'DocumentCard.Checked' })}</div>
        )}
      </div>

      {isMobile ? (
        <div className={b('actions')}>
          <DocumentActions
            onCheckmarkClick={onCheckmarkClick}
            onRenameClick={onRenameClick}
            onRemove={onRemove}
            isChecked={isChecked}
            handlers={handlers}
          />
        </div>
      ) : (
        <DocumentActions
          onCheckmarkClick={onCheckmarkClick}
          onRenameClick={onRenameClick}
          onRemove={onRemove}
          isChecked={isChecked}
          handlers={handlers}
        />
      )}
    </div>
  )
}
